import { computed, type Ref, ref, watch } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import {
  ACTIVE_TABLE_STATUSES,
  type Reservation,
  type ReservationRequest,
} from "@lemmonhq/types";
import { getRestaurantDayRangeUtc } from "@lemmonhq/blogic";
import { useTrpc } from "../../providers";

export const tablesQueryKeys = {
  all: ["tables"] as const,
  status: (tableIds?: number[]) =>
    [
      "tables",
      "status",
      tableIds?.length ? [...tableIds].sort() : "all",
    ] as const,
  reservations: {
    all: ["tables", "reservations"] as const,
    day: (range: { date_from: string; date_to: string }) =>
      [
        "tables",
        "reservations",
        "day",
        range.date_from,
        range.date_to,
      ] as const,
    requestsDay: (range: { date_from: string; date_to: string }) =>
      [
        "tables",
        "reservation-requests",
        "day",
        range.date_from,
        range.date_to,
      ] as const,
  },
};

export function useTablesSelectedDayData(input: {
  selectedDate: Ref<Date>;
  restaurantTz: Ref<string>;
}) {
  const trpc = useTrpc();
  const queryClient = useQueryClient();
  const reservations = ref<Reservation[]>([]);
  const pendingRequests = ref<ReservationRequest[]>([]);
  const isLoadingReservations = ref(false);

  const dateRange = computed(() =>
    getRestaurantDayRangeUtc(
      input.selectedDate.value,
      input.restaurantTz.value,
    ),
  );

  const refetchSelectedDay = async () => {
    isLoadingReservations.value = true;
    try {
      const [resResult, reqResult] = await Promise.all([
        trpc.reservations.list.query({
          ...dateRange.value,
          status: [...ACTIVE_TABLE_STATUSES],
          limit: 100,
        }),
        trpc.reservations.listRequests.query({
          status: "pending",
          ...dateRange.value,
          limit: 100,
        }),
      ]);
      reservations.value = resResult.data || [];
      const reqData = reqResult as any;
      pendingRequests.value = Array.isArray(reqData)
        ? reqData
        : reqData?.data || [];
    } catch (error) {
      console.error("Failed to fetch selected day data:", error);
    } finally {
      isLoadingReservations.value = false;
    }
  };

  const invalidateTablesData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: tablesQueryKeys.all }),
      queryClient.invalidateQueries({
        queryKey: tablesQueryKeys.reservations.all,
      }),
      queryClient.invalidateQueries({ queryKey: ["reservationCounts"] }),
      queryClient.invalidateQueries({ queryKey: ["reservations"] }),
      queryClient.invalidateQueries({ queryKey: ["reservationRequests"] }),
    ]);
  };

  // Watch for date/timezone changes to reload data
  watch(
    [input.selectedDate, input.restaurantTz],
    () => {
      void refetchSelectedDay();
    },
    { immediate: true },
  );

  return {
    dateRange,
    reservations,
    pendingRequests,
    isLoadingReservations,
    refetchSelectedDay,
    invalidateTablesData,
  };
}
